import { Typography, message, Image, Row, Col, Flex } from 'antd';
import { updateAttributesOfCdnImage } from '@admin-panel/utils';
import { WorkflowDTOType, WorkflowItemDTOType } from '../../../../../schemas';
import { useUpdateWorkflowItemMutation } from '../../../../../store/workflows/workflowApi';
import styled from '@emotion/styled';
import AcceptedTags from './AcceptedTags';
import ProposedTags from './ProposedTags';
import RejectedTags from './RejectedTags';
import TagsForQC from './TagsForQC';
import { ImageMask, useImageMask } from '@admin-panel/components';
import { tagFamilyToTreeData } from './utils';

const Card = styled.div`
  border: 1px solid #f1f3f5;
  border-radius: 16px;
  padding: 8px;
  background: #fff;
`;

const NoImage = styled.div`
  width: 60px;
  height: 90px;
  border-radius: 8px;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
  font-size: 12px;
`;

interface IWorkflowItemHorizontalProps {
  data: WorkflowItemDTOType;
  workflow: WorkflowDTOType;
  purpose: 'PENDING' | 'PASSED' | 'REJECTED' | 'PROCESSING';
  lastItemRef?: (element: HTMLElement | null) => void;
}

const WorkflowItemHorizontal = ({
  data,
  workflow,
  purpose,
  lastItemRef,
}: IWorkflowItemHorizontalProps) => {
  const [updateItem, { isLoading }] = useUpdateWorkflowItemMutation();

  const {
    isPreviewOpen,
    setIsPreviewOpen,
    previewCoords,
    handleMaskMouseEnter,
    handleMaskMouseLeave,
  } = useImageMask({
    magnificationFactor: 50,
  });

  const rejectedTags = data.rejectedTags ?? [];
  const acceptedTags = data.acceptedTags ?? [];
  const proposedTags = data.proposedTags ?? [];

  const treeData = tagFamilyToTreeData(workflow.tagFamily);

  const handleAcceptedTagsChange = (newAccepted: Array<string>) => {
    const toRejected = acceptedTags.filter(tag => !newAccepted.includes(tag));

    const toAccepted = newAccepted.filter(tag => !acceptedTags.includes(tag));

    const finalProposed = proposedTags.filter(tag => !toAccepted.includes(tag));
    const finalRejected = rejectedTags.filter(tag => !toAccepted.includes(tag));

    const updatedRejected = Array.from(new Set([...finalRejected, ...toRejected]));

    updateItem({
      workflowId: data.workflowId,
      runId: data.workflowRunId,
      itemId: data.id,
      acceptedTags: newAccepted,
      rejectedTags: updatedRejected,
      proposedTags: finalProposed,
    })
      .unwrap()
      .then(() => message.success('Tags updated'))
      .catch(() => message.error('Failed to update tags'));
  };

  const moveTag = async (
    tags: string | Array<string>,
    source: 'proposed' | 'accepted' | 'rejected',
    destination: 'proposed' | 'accepted' | 'rejected' | '',
  ) => {
    const tagList = Array.isArray(tags) ? tags : [tags];

    let updatedProposed = [...proposedTags];
    let updatedAccepted = [...acceptedTags];
    let updatedRejected = [...rejectedTags];

    // Remove tags from source
    if (source === 'proposed') {
      updatedProposed = updatedProposed.filter(t => !tagList.includes(t));
    }
    if (source === 'accepted') {
      updatedAccepted = updatedAccepted.filter(t => !tagList.includes(t));
    }
    if (source === 'rejected') {
      updatedRejected = updatedRejected.filter(t => !tagList.includes(t));
    }

    // Add tags to destination (if not already present and destination is not '')
    if (destination && destination === 'proposed') {
      tagList.forEach(tag => {
        if (!updatedProposed.includes(tag)) updatedProposed.push(tag);
      });
    }
    if (destination && destination === 'accepted') {
      tagList.forEach(tag => {
        if (!updatedAccepted.includes(tag)) updatedAccepted.push(tag);
      });
    }
    if (destination && destination === 'rejected') {
      tagList.forEach(tag => {
        if (!updatedRejected.includes(tag)) updatedRejected.push(tag);
      });
    }

    try {
      await updateItem({
        workflowId: data.workflowId,
        runId: data.workflowRunId,
        itemId: data.id,
        acceptedTags: updatedAccepted,
        rejectedTags: updatedRejected,
        proposedTags: updatedProposed,
      }).unwrap();
      message.success(
        Array.isArray(tags)
          ? `Tags moved${destination ? ` to ${destination}` : ''}`
          : `Tag moved${destination ? ` to ${destination}` : ''}`,
      );
    } catch {
      message.error('Failed to move tag(s)');
    }
  };

  const renderTagsSection = () => {
    switch (purpose) {
      case 'PROCESSING': {
        return proposedTags.length > 0 ? (
          <ProposedTags tags={proposedTags} closable={false} />
        ) : (
          <Typography.Text type="secondary">Awaiting AI tagging</Typography.Text>
        );
      }

      case 'PENDING':
        return (
          <TagsForQC
            workflowId={workflow.id}
            runId={data.workflowRunId}
            itemId={data.id}
            proposedTags={proposedTags}
            acceptedTags={acceptedTags}
            rejectedTags={rejectedTags}
            allTagOptions={treeData}
          />
        );

      case 'PASSED':
        return (
          <>
            <AcceptedTags
              tags={acceptedTags}
              closable
              treeData={treeData}
              onCloseTag={tag => moveTag(tag, 'accepted', 'rejected')}
              closeTooltip="Move to rejected tags"
              onTagChange={handleAcceptedTagsChange}
              isLoading={isLoading}
            />
            <RejectedTags tags={rejectedTags} closable={false} />
          </>
        );

      case 'REJECTED':
        return (
          <>
            <AcceptedTags tags={acceptedTags} closable={false} treeData={treeData} />
            <RejectedTags
              tags={rejectedTags}
              closable
              onCloseTag={tag => moveTag(tag, 'rejected', 'accepted')}
              closeTooltip="Move to accepted tags"
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Card ref={lastItemRef}>
      <Row gutter={16} align="top" wrap={false}>
        <Col flex="80px">
          {data.images && data.images.length > 0 ? (
            <Image
              src={updateAttributesOfCdnImage(data.images[0].url, {
                width: 360,
                quality: 100,
              })}
              onMouseEnter={handleMaskMouseEnter}
              onMouseLeave={handleMaskMouseLeave}
              alt="Product"
              width={60}
              height={90}
              style={{
                objectFit: 'cover',
                borderRadius: 8,
              }}
              preview={
                data.images[0].url
                  ? {
                      mask: (
                        <ImageMask
                          fileUrl={updateAttributesOfCdnImage(data.images[0].url, {
                            width: 1080,
                            quality: 100,
                          })}
                          magnificationFactor={50}
                          isPreviewOpen={isPreviewOpen}
                          setIsPreviewOpen={setIsPreviewOpen}
                          previewCoords={previewCoords}
                        />
                      ),
                      maskClassName: 'image-mask',
                    }
                  : false
              }
            />
          ) : (
            <NoImage>No Image</NoImage>
          )}
        </Col>
        <Col flex="25%">
          <Flex vertical gap={2}>
            <Typography.Text strong style={{ textTransform: 'uppercase' }}>
              {data.brandName}
            </Typography.Text>
            <Typography.Text>{data.name}</Typography.Text>
            <Typography.Text style={{ fontWeight: 600, fontSize: 12 }}>₹{data.mrp}</Typography.Text>
          </Flex>
        </Col>
        <Col flex="1">
          <Flex gap={24}>{renderTagsSection()}</Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default WorkflowItemHorizontal;
